import {
  AspectRatio,
  Box,
  BoxProps,
  Image,
  ImageProps,
  Skeleton,
} from '@chakra-ui/react';
import { NestedArea } from '@habity/types';
import { useInView } from 'react-intersection-observer';
import { getCloudflareStaticMapUrlForArea } from '~/lib/providers/cloudflare/images';

interface AreaImageCardProps {
  area: NestedArea;
  boxProps?: BoxProps;
  aspectRatio?: number;
  borderTopRadius?: string;
  borderBottomRadius?: string;
  imageProps?: ImageProps;
  isLoading?: boolean;
  spacing?: 'default' | 'compact';
}

export const AreaImageCard = ({
  area,
  boxProps,
  aspectRatio = 1 / 1.2,
  borderTopRadius = '4px',
  borderBottomRadius = '4px',
  imageProps,
  isLoading = false,
  spacing = 'default',
}: AreaImageCardProps) => {
  const { name } = area || {};

  const imageUrl = getCloudflareStaticMapUrlForArea(area);

  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0,
  });

  if (isLoading) {
    return (
      <Box
        position="relative"
        borderTopRadius={borderTopRadius}
        borderBottomRadius={borderBottomRadius}
        overflow="hidden"
        {...boxProps}
      >
        <AspectRatio ratio={aspectRatio}>
          <Skeleton height="100%" width="100%" />
        </AspectRatio>
      </Box>
    );
  }

  // FIXME: We shouldn't need to duplicate the whole component for a simple conditional
  // render BUT forced to do it this way because otherwise we get a "destroy" error
  // which seems to point at plasmic but is clearly related to this

  if (!imageUrl) {
    return (
      <Box
        position="relative"
        borderRadius="4px"
        overflow="hidden"
        width={spacing === 'compact' ? '120px' : '100%'}
        backgroundColor="blackAlpha.50"
        {...boxProps}
      >
        <AspectRatio ratio={aspectRatio}>
          <Box />
        </AspectRatio>
      </Box>
    );
  }

  return (
    <Box
      ref={ref}
      position="relative"
      borderRadius="4px"
      overflow="hidden"
      width={spacing === 'compact' ? '120px' : '100%'}
      backgroundColor="blackAlpha.50"
      {...boxProps}
    >
      {inView ? (
        <AspectRatio ratio={aspectRatio}>
          <Image
            src={imageUrl}
            alt={name || 'Area image'}
            objectFit="cover"
            objectPosition="top"
            width="100%"
            height="100%"
            {...imageProps}
          />
        </AspectRatio>
      ) : (
        <Box minH="300px" backgroundColor="blackAlpha.50" />
      )}
    </Box>
  );
};
